/* eslint-disable react-refresh/only-export-components */
import * as React from "react"

import { subscribeAuth } from "@/lib/api/client"
import {
  clearTokens,
  readTokens,
  writeTokens,
} from "@/lib/storage/auth-storage"
import type { TokenPair } from "@/features/auth/api/auth.api"

interface AuthContextValue {
  isAuthenticated: boolean
  setSession: (tokens: TokenPair) => void
  signOut: () => void
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    () => readTokens() !== null
  )

  React.useEffect(() => {
    return subscribeAuth((event) => {
      if (event === "logout") {
        setIsAuthenticated(false)
      }
    })
  }, [])

  const setSession = React.useCallback((tokens: TokenPair) => {
    writeTokens({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresIn: tokens.expiresIn,
    })
    setIsAuthenticated(true)
  }, [])

  const signOut = React.useCallback(() => {
    clearTokens()
    setIsAuthenticated(false)
  }, [])

  const value = React.useMemo(
    () => ({ isAuthenticated, setSession, signOut }),
    [isAuthenticated, setSession, signOut]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = React.useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
